import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BgBomCategory, BgBomGroupedSubCategory, BgBomItem, BgBomModel, BgBomSubCategory, BomType } from '../models/bg.model';
import { BackendApiService } from './backend-api.service';

@Injectable({
  providedIn: 'root'
})
export class BomItemService {
  private _lastItem?: any;

  constructor(
    private bgModelService: BackendApiService
  ) { }

  /**
   * Sets flat index to each item in the category for display purposes
   */
  setFlatIndex(category: BgBomCategory): void {
    let flatIndex = 1;
    category.bgGroupedSubCategories?.forEach((group) => {
      group.bgSubCategories?.forEach((sub) => {
        sub.bgBomItems.forEach((item) => {
          item.flatIndex = flatIndex++;
        });
      });
    });
  }

  /**
   * Get filtered subcategories based on search term
   */
  getFilteredGroupedSubCategories(
    selectedCategory?: BgBomCategory,
    filterText?: string
  ): BgBomGroupedSubCategory[] | undefined {
    const normalizedFilterText = filterText?.toLowerCase().replace(/\s/g, '') || '';

    if (!selectedCategory || !selectedCategory.bgGroupedSubCategories) {
      return selectedCategory?.bgGroupedSubCategories;
    }

    if (!normalizedFilterText) {
      return selectedCategory.bgGroupedSubCategories;
    }

    // Helper function to check if an item matches the filter
    const itemMatchesFilter = (item: BgBomItem): boolean => {
      return !!(
        item.itemName?.toLowerCase().replace(/\s/g, '').includes(normalizedFilterText) ||
        item.description?.toLowerCase().replace(/\s/g, '').includes(normalizedFilterText) ||
        item.size?.toLowerCase().replace(/\s/g, '').includes(normalizedFilterText)
      );
    };

    // Helper function to check if a subcategory matches
    const subcategoryMatchesFilter = (subcategory: BgBomSubCategory): boolean => {
      // Check if this subcategory's name matches
      if (subcategory.name?.toLowerCase().replace(/\s/g, '').includes(normalizedFilterText)) {
        return true;
      }

      // Check if any items in this subcategory match
      return subcategory.bgBomItems.some(itemMatchesFilter);
    };

    // Helper function to get filtered subcategories
    const getFilteredSubcategories = (subcategory: BgBomSubCategory): BgBomSubCategory | null => {
      // If this subcategory matches, include it with all its items
      if (subcategory.name?.toLowerCase().replace(/\s/g, '').includes(normalizedFilterText)) {
        return subcategory;
      }

      // Filter items in this subcategory
      const filteredItems = subcategory.bgBomItems.filter(itemMatchesFilter);

      // If we have matching items, return the filtered subcategory
      if (filteredItems.length > 0) {
        return {
          ...subcategory,
          bgBomItems: filteredItems
        };
      }

      return null;
    };

    return selectedCategory.bgGroupedSubCategories
      .map(groupedSubcategory => {
        // Check if the grouped subcategory name matches
        if (groupedSubcategory.name?.toLowerCase().replace(/\s/g, '').includes(normalizedFilterText)) {
          return groupedSubcategory;
        }

        const filteredBgSubCategories = groupedSubcategory?.bgSubCategories
          ?.map(getFilteredSubcategories)
          .filter(subcategory => subcategory != null) as BgBomSubCategory[];

        if (filteredBgSubCategories.length > 0) {
          return {
            ...groupedSubcategory,
            bgSubCategories: filteredBgSubCategories
          };
        } else {
          return null;
        }
      })
      .filter(groupedSubcategory => groupedSubcategory != null) as BgBomGroupedSubCategory[];
  }

  /**
   * Check if an item is a duplicate within the same category
   */
  isDuplicate(
    item: BgBomItem,
    property: keyof BgBomItem,
    resetOnUnique: boolean = false
  ) {
    const propertyValue = item[property];

    if (this._lastItem && property in this._lastItem) {
      if (propertyValue === this._lastItem[property]) {
        return true;
      }

      if (resetOnUnique) {
        this._lastItem = { [property]: propertyValue };
      }
    }
    this._lastItem = { ...this._lastItem, [property]: propertyValue };
    return false;
  }

  /**
   * Reset the duplicate tracking state
   */
  resetDuplicates() {
    this._lastItem = null;
  }

  /**
   * Add a custom line item to a subcategory
   */
  addCustomLine(subcategory: BgBomSubCategory): BgBomItem {
    let order = Math.max(...subcategory.bgBomItems.map((i) => i.order ?? -1));
    const newItem = {
      id: undefined,
      group: subcategory.name,
      order: order + 1,
      description: '',
      itemName: '',
      manufacturer: '',
      manufacturerModel: '',
      material: '',
      modelQuantity: '',
      modelQuantityDescription: '',
      parameters: [],
      quantity: 0,
      quantityToOrder: '',
      quantityToOrderDescription: '',
      size: '',
      itemBomType: BomType.Addon,
      isEditMode: true,
      wasteFactor: '',
    };

    subcategory.bgBomItems.push(newItem);
    return newItem;
  }

  /**
   * Save a custom item to the backend
   */
  saveCustomItem(modelId: string, item: BgBomItem, subcategory: BgBomSubCategory): Observable<any> {
    item.isEditMode = false;
    item.showLoader = true;
    return this.bgModelService.AddCustomItem(modelId, subcategory.path, item);
  }

  /**
   * Delete a custom item
   */
  deleteCustomItem(modelId: string, item: BgBomItem): Observable<any> {
    return this.bgModelService.DeleteCustomItem(modelId, item);
  }

  /**
   * Find the subcategory that contains the given item
   */
  findSubcategoryForItem(item: BgBomItem, category?: BgBomCategory): BgBomSubCategory | undefined {
    if (!category || !category.bgGroupedSubCategories) {
      return undefined;
    }

    for (const group of category.bgGroupedSubCategories) {
      for (const subcat of (group.bgSubCategories ?? [])) {
        if (subcat.bgBomItems.includes(item)) {
          return subcat;
        }
      }
    }
    return undefined;
  }


  findGroupedSubcategoryForItem(item: BgBomItem, category?: BgBomCategory): BgBomGroupedSubCategory | undefined {
    if (!category || !category.bgGroupedSubCategories) {
      return undefined;
    }

    for (const group of category.bgGroupedSubCategories) {
      if (group.bgSubCategories?.some(sub => sub.bgBomItems.includes(item))) {
        return group;
      }
    }
    return undefined;
  }

  /**
   * Convert categories to tabs for UI display
   */
  categoriesToTabs(
    categories?: Array<BgBomCategory>,
    onSelect?: (category: BgBomCategory) => void
  ): Array<any> | undefined {
    return categories?.map(
      (e) => ({
        name: e.name,
        click: () => onSelect && onSelect(e),
      })
    );
  }
}
